import { AlertCircle, CheckCircle2, CircleDashed, Clock, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Canonical → display bucket mapping. The wire is one of:
 *   started · reserved · paid · confirmed · cancelled · completed · refunded · refund_failed
 * `started` is filtered out by the orders API; we still handle it defensively.
 */
const DISPLAY_BUCKET = {
	started: 'Pending',
	reserved: 'Pending',
	paid: 'Pending',
	confirmed: 'Confirmed',
	completed: 'Completed',
	cancelled: 'Cancelled',
	refunded: 'Cancelled',
	refund_failed: 'Refund failed'
};

const BUCKET_STYLES = {
	Pending: { pill: 'bg-warning-soft text-warning-soft-foreground', Icon: Clock },
	Confirmed: { pill: 'bg-info-soft text-info-soft-foreground', Icon: CircleDashed },
	Completed: { pill: 'bg-success-soft text-success-soft-foreground', Icon: CheckCircle2 },
	Cancelled: { pill: 'bg-muted text-muted-foreground', Icon: XCircle },
	'Refund failed': { pill: 'bg-destructive/10 text-destructive', Icon: AlertCircle }
};

export const ORDER_DISPLAY_BUCKETS = ['Pending', 'Confirmed', 'Completed', 'Cancelled'];

/**
 * Expand a list of display buckets to the canonical wire statuses they cover.
 * Useful for applying a checkbox filter back against the orders list.
 */
export function expandBuckets(buckets) {
	const expanded = new Set();
	for (const bucket of buckets) {
		for (const [wire, display] of Object.entries(DISPLAY_BUCKET)) {
			if (display === bucket) expanded.add(wire);
		}
	}
	return expanded;
}

export function bucketFor(status) {
	return DISPLAY_BUCKET[status] || 'Pending';
}

export function StatusPill({ status }) {
	const bucket = bucketFor(status);
	const cfg = BUCKET_STYLES[bucket] || BUCKET_STYLES.Pending;
	const { Icon } = cfg;
	// Lock the pill to a fixed `h-6` (24px) so icon-specific glyph metrics —
	// CircleDashed's segments render slightly taller than CheckCircle2/XCircle
	// at the same `h-3.5` — can't bump the box height between status types.
	// `leading-none` keeps the text from contributing its own line-box height.
	return (
		<span className={cn('inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold leading-none', cfg.pill)}>
			<Icon className="h-3.5 w-3.5 shrink-0" />
			{bucket}
		</span>
	);
}
